/* eslint-disable react/prop-types */
import React, { FC, ReactNode, useMemo, useState } from "react";
import { css, styled } from "@mui/system";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { COLORS, FONTS, FONT_FAMILIES, FONT_WEIGHTS, RADII, SHADOWS, SPACINGS, getColors } from "../../common/consts";
import { useTheme } from "../../common/contexts/ThemeContext";
import { makeShouldForwardProps } from "../../common/utils/ui";
import { formatShortDate } from "../../common/utils/date-time";
import Card from "../Display/Card";
import Text, { TextVariant } from "../Display/Text";
import Button, { ButtonVariants } from "../Display/Button";
import { ChartSkeleton, NumberBone } from "../Display/Skeletons";
import Tooltip from "../Display/Tooltip";
import { CircleSvg, DiamondSvg, SquareSvg, TriangleSvg } from "../Display/Shapes";

/**
 * The Insights report layout, generalized: a column of clickable stat
 * sections on the left and a line chart for the selected section on the
 * right. Same shapes, colors, legend pills, and tooltip as the email report,
 * so every report on the page reads the same.
 */

export interface MetricSeries {
  key: string;
  label: string;
  color: string;
  shape?: FC;
  format?: (value: number) => string;
}

export interface MetricStat {
  key: string;
  label: string;
  value: string | number;
  color?: string;
  onClick?: () => void;
}

export interface MetricSection {
  key: string;
  title: string;
  stats: MetricStat[];
  series: MetricSeries[];
  /** Rendered under the stats, for a call to action such as Subscribe. */
  footer?: ReactNode;
}

export type MetricsRow = { date: string } & Record<string, number | string>;

interface Props {
  sections: MetricSection[];
  data: MetricsRow[];
  isLoading?: boolean;
  initialSection?: string;
  /** The x-axis labels; dates by default (a live chart passes its clock times through). */
  xTickFormatter?: (value: string) => string;
}

const SHAPES: FC[] = [DiamondSvg, TriangleSvg, SquareSvg, CircleSvg];

const NoPaddingCard = styled(Card)`
  padding: ${SPACINGS.none};
`;

const Sections = styled("div")`
  min-width: 15rem;
  max-width: 19rem;
`;

interface SectionProps {
  active?: boolean;
  className?: string;
  onClick?: () => void;
  children?: ReactNode;
}

const shouldForwardProp = makeShouldForwardProps<SectionProps>(["active"]);
const Section = styled(({ active, ...rest }: SectionProps) => <div {...rest} />, { shouldForwardProp })`
  padding: ${SPACINGS.xl};
  border-bottom: 1px solid var(--color-backgroundBrand, ${COLORS.backgroundBrand});
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: ${SPACINGS.md};

  &:last-of-type {
    border-bottom: none;
  }

  &:hover {
    background-color: var(--color-backgroundBrandTabHover, ${COLORS.backgroundBrandTabHover});
  }

  /* Outreach's own selected stat treatment: brand fill with a brand rule. */
  ${({ active }) =>
    active &&
    css`
      box-shadow: ${SHADOWS.inset} var(--crm-accent, ${SHADOWS.brand});
      background-color: var(--color-backgroundBrandLight, ${COLORS.backgroundBrandLight});
      color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});

      &:hover {
        background-color: var(--color-backgroundBrandLight, ${COLORS.backgroundBrandLight});
      }
    `}
`;

const TitleText = styled(Text)`
  color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
  font: ${FONTS.sm};
  font-weight: ${FONT_WEIGHTS.medium};
  letter-spacing: 0.025rem;
  margin: 0;
`;

const Stats = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACINGS.lg} ${SPACINGS.xl};
`;

const Stat = styled("div")`
  min-width: 6.5rem;
  display: flex;
  flex-direction: column;
  line-height: 1.5rem;
`;

const NumberText = styled(Text)`
  margin: 0;
  font-family: ${FONT_FAMILIES.titles};
  font-size: 1.5rem;
  font-weight: ${FONT_WEIGHTS.semibold};
  line-height: 2.25rem;
  color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
`;

const SubtitleText = styled(Text)`
  color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.875rem;
  font-weight: ${FONT_WEIGHTS.regular};
  line-height: 1.25rem;
  margin: 0;
`;

const ChartContainer = styled("div")`
  display: flex;
  align-items: stretch;
  border-left: 1px solid var(--color-borderPrimary, ${COLORS.borderPrimary});
  overflow: hidden;
  min-height: 320px;

  .recharts-responsive-container {
    flex: 1 1 auto;
  }
`;

const LegendList = styled("div")`
  justify-content: flex-end;
  align-items: center;
  background-color: var(--color-backgroundWhite, var(--color-backgroundPrimary, ${COLORS.backgroundPrimary}));
  padding: ${SPACINGS.sm} ${SPACINGS.md};
  border-radius: ${RADII.md} ${RADII.md} 0 0;
  display: flex;
  gap: ${SPACINGS.sm};
  flex-wrap: wrap;
`;

const LegendButton = styled(Button)`
  &&.MuiButton-root {
    padding: 0.7rem;
    font: ${FONTS.sm};
    font-weight: 300;
  }

  .MuiButton-startIcon {
    margin-right: 0.2rem;
  }
`;

const ShapeWrapper = styled("div")<{ color: string }>`
  ${({ color }) => css`
    color: ${color};
  `};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TooltipItem = styled("div")`
  display: flex;
  align-items: center;
  margin-bottom: ${SPACINGS.xs};
  color: #f9fafb;
  font-weight: 400;
`;

const TooltipLabel = styled("span")`
  font-weight: 300;
  margin: 0 4px 0 ${SPACINGS.sm};
`;

const TooltipValue = styled("span")`
  font-weight: 500;
`;

const ANIMATION_THRESHOLD = 14;

const MetricsPanel: FC<Props> = ({ sections, data, isLoading, initialSection, xTickFormatter = formatShortDate }) => {
  const { mode } = useTheme();
  const colors = getColors(mode);
  const [selected, setSelected] = useState<string>(initialSection || sections[0]?.key);
  const [hidden, setHidden] = useState<Record<string, boolean>>({});
  const section = useMemo(() => sections.find((s) => s.key === selected) || sections[0], [sections, selected]);
  const isAnimationActive = data.length <= ANIMATION_THRESHOLD;

  const seriesWithShapes = useMemo(
    () => (section?.series || []).map((s, i) => ({ ...s, shape: s.shape || SHAPES[i % SHAPES.length] })),
    [section]
  );

  const renderLegend = () => (
    <LegendList className="w-100 d-flex">
      {seriesWithShapes.map((s) => {
        const Shape = s.shape as FC;
        return (
          <div key={s.key} onClick={() => setHidden((h) => ({ ...h, [s.key]: !h[s.key] }))}>
            <LegendButton
              variant={ButtonVariants.pill}
              active={!!hidden[s.key]}
              startIcon={
                <ShapeWrapper color={hidden[s.key] ? COLORS.foregroundSecondaryLight : s.color}>
                  <Shape />
                </ShapeWrapper>
              }
            >
              {s.label}
            </LegendButton>
          </div>
        );
      })}
    </LegendList>
  );

  const renderTooltip = (props: { active?: boolean; label?: string; payload?: Array<{ name: string; value: number }> }) => (
    <Tooltip
      open={props.active}
      title={props.label}
      content={(props.payload || []).map((entry) => {
        const s = seriesWithShapes.find((x) => x.key === entry.name);
        if (!s) return null;
        const Shape = s.shape as FC;
        return (
          <TooltipItem key={entry.name}>
            <ShapeWrapper color={s.color}>
              <Shape />
            </ShapeWrapper>
            <TooltipLabel>{s.label}:</TooltipLabel>
            <TooltipValue>{s.format ? s.format(Number(entry.value || 0)) : entry.value}</TooltipValue>
          </TooltipItem>
        );
      })}
    />
  );

  return (
    <NoPaddingCard className="d-flex">
      <Sections>
        {sections.map((s) => (
          <Section key={s.key} active={s.key === section?.key} onClick={() => setSelected(s.key)}>
            <TitleText variant={TextVariant.sm} className="text-uppercase">
              {s.title}
            </TitleText>
            <Stats>
              {s.stats.map((stat) => (
                <Stat key={stat.key} onClick={stat.onClick}>
                  <NumberText variant={TextVariant.xl} color={stat.color}>
                    {isLoading ? <NumberBone /> : stat.value}
                  </NumberText>
                  <SubtitleText variant={TextVariant.sm}>{stat.label}</SubtitleText>
                </Stat>
              ))}
            </Stats>
            {s.footer}
          </Section>
        ))}
      </Sections>
      <ChartContainer className="position-relative flex-grow-1 py-2 pl-3 pr-0">
        {isLoading ? (
          /* The chart's own frame — legend, axes, plot — while the series load. */
          <ChartSkeleton series={(section?.series || []).length || 3} />
        ) : (
          <div style={{ width: "100%", height: "100%", minWidth: 100, display: "flex", flexDirection: "column" }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={300}>
              <LineChart data={data} key={section?.key} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
                <CartesianGrid stroke="transparent" />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={{ stroke: colors.chartAxisLine }}
                  tick={{ fill: colors.chartAxisColor, fontSize: "0.875rem", fontWeight: 400, fontFamily: FONT_FAMILIES.regular }}
                  tickFormatter={xTickFormatter}
                  minTickGap={24}
                  padding={{ left: 20, right: 20 }}
                />
                <YAxis
                  includeHidden
                  allowDecimals={false}
                  width={52}
                  tickLine={false}
                  axisLine={{ stroke: colors.chartAxisLine }}
                  tick={{ fill: colors.chartAxisColor, fontSize: "0.875rem", fontWeight: 400, fontFamily: FONT_FAMILIES.regular }}
                  padding={{ top: 5, bottom: 5 }}
                />
                <RechartsTooltip content={renderTooltip as any} />
                <Legend verticalAlign="top" content={renderLegend} />
                {seriesWithShapes.map((s) => (
                  <Line
                    key={s.key}
                    type="linear"
                    dataKey={s.key}
                    stroke={s.color}
                    dot={false}
                    isAnimationActive={isAnimationActive}
                    strokeWidth={2}
                    hide={!!hidden[s.key]}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartContainer>
    </NoPaddingCard>
  );
};

export default MetricsPanel;
